import { PrincipalDocumentScope, PrincipalType } from '~/swagger/companies/Api';
import type { BetterForm } from '@magicdoorinc/form';
import type { BusinessInfoDraftDto, BusinessInfoRequirementDto } from '~/swagger/companies/Api';
import type {
  BusinessInfoFileFormValue,
  CompanyBusinessInfoFormPayload,
  PrincipalFileFormValue,
  PrincipalFormValue,
} from '~/utils/companyBusinessInfoPayload';

export type CompanyBusinessInfoValidationMode = 'draftSave' | 'submit';

export type CompanyBusinessInfoValidationResult = {
  valid: boolean;
  firstErrorPath?: BetterForm.NamePath;
  fieldErrors: Record<string, string>;
};

type CompanyBusinessInfoErrorForm = {
  clearValidate: () => void;
  setFormError: (namePath: BetterForm.NamePath, error: string | undefined) => void;
};

type ValidateCompanyBusinessInfoFormParams = {
  payload: CompanyBusinessInfoFormPayload;
  draft?: BusinessInfoDraftDto | null;
  requirement?: BusinessInfoRequirementDto;
  mode: CompanyBusinessInfoValidationMode;
  t: (message: string, params?: Record<string, string | number>) => string;
};

const MASK_CHARACTER = '•';

const pathToKey = (path: BetterForm.NamePath) => (Array.isArray(path) ? path.join('.') : String(path));

const keyToPath = (key: string): BetterForm.NamePath =>
  key.split('.').map((segment) => {
    const index = Number(segment);
    return Number.isNaN(index) ? segment : index;
  });

const isMaskedValue = (value: unknown) => typeof value === 'string' && value.includes(MASK_CHARACTER);

const trimOptional = (value: string | undefined) => {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const normalizeMaskedString = (value: string | undefined) => {
  if (isMaskedValue(value)) return undefined;
  return value;
};

const normalizeDriverLicense = (driversLicense: PrincipalFormValue['driversLicense']) => {
  if (!driversLicense) return undefined;

  const number = normalizeMaskedString(trimOptional(driversLicense.number));
  const state = trimOptional(driversLicense.state);
  if (!number && !state) return undefined;

  return { ...(number ? { number } : {}), ...(state ? { state } : {}) };
};

const normalizePrincipal = (principal: PrincipalFormValue): PrincipalFormValue => ({
  ...principal,
  email: normalizeMaskedString(trimOptional(principal.email)),
  phone: normalizeMaskedString(trimOptional(principal.phone)),
  ssn: normalizeMaskedString(trimOptional(principal.ssn)),
  dateOfBirth: normalizeMaskedString(trimOptional(principal.dateOfBirth)),
  driversLicense: normalizeDriverLicense(principal.driversLicense),
});

export const normalizeCompanyBusinessInfoPayload = (payload: CompanyBusinessInfoFormPayload): CompanyBusinessInfoFormPayload => ({
  ...payload,
  legalName: trimOptional(payload.legalName),
  ein: normalizeMaskedString(trimOptional(payload.ein)),
  mccCode: trimOptional(payload.mccCode),
  summary: trimOptional(payload.summary),
  startDate: trimOptional(payload.startDate),
  principals: payload.principals?.map(normalizePrincipal),
});

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^\D*(\d\D*){10}$/;
const einPattern = /^\d{2}-?\d{7}$/;
const ssnPattern = /^\d{3}-?\d{2}-?\d{4}$/;
const datePattern = /^\d{4}-\d{2}-\d{2}$|^\d{2}\/\d{2}\/\d{4}$/;

const visibleFileCount = (files: { deleted?: string }[] | undefined) => files?.filter((file) => file.deleted !== 'deleted').length ?? 0;

const hasValue = (value: unknown) => (typeof value === 'string' ? value.trim().length > 0 : value !== undefined && value !== null);
const hasRequiredValue = (value: unknown) => isMaskedValue(value) || hasValue(value);

const principalRequiresObligation = (principal: PrincipalFormValue, obligationScope: PrincipalDocumentScope) => {
  if (obligationScope === PrincipalDocumentScope.ControlPerson) return principal.type === PrincipalType.ControlPerson;
  if (obligationScope === PrincipalDocumentScope.BeneficialOwners) return principal.type === PrincipalType.BeneficialOwner;
  return true;
};

export const validateCompanyBusinessInfoForm = ({
  payload,
  requirement,
  mode,
  t,
}: ValidateCompanyBusinessInfoFormParams): CompanyBusinessInfoValidationResult => {
  const normalized = normalizeCompanyBusinessInfoPayload(payload);
  const fieldErrors: Record<string, string> = {};
  let firstErrorPath: BetterForm.NamePath | undefined;

  const addError = (path: BetterForm.NamePath, message: string) => {
    const key = pathToKey(path);
    if (fieldErrors[key]) return;
    fieldErrors[key] = message;
    firstErrorPath ??= path;
  };

  const addFormatError = (path: BetterForm.NamePath, value: string | undefined, pattern: RegExp, message: string) => {
    if (!value || isMaskedValue(value)) return;
    if (!pattern.test(value.trim())) {
      addError(path, message);
    }
  };

  addFormatError(['ein'], normalized.ein, einPattern, t('EIN is not valid'));

  normalized.principals?.forEach((principal, principalIndex) => {
    addFormatError(['principals', principalIndex, 'email'], principal.email, emailPattern, t('Email is not valid'));
    addFormatError(['principals', principalIndex, 'phone'], principal.phone, phonePattern, t('Phone is not valid'));
    addFormatError(['principals', principalIndex, 'ssn'], principal.ssn, ssnPattern, t('SSN is not valid'));
    addFormatError(
      ['principals', principalIndex, 'dateOfBirth'],
      principal.dateOfBirth,
      datePattern,
      t('Date of birth is invalid. Age must be between 18 and 125 years.')
    );

    if (principal.ownership !== undefined && (principal.ownership < 0 || principal.ownership > 100)) {
      addError(
        ['principals', principalIndex, 'ownership'],
        t('{name}: {min}-{max}, multiples of {step}', { name: t('Ownership'), min: 0, max: 100, step: 1 })
      );
    }
  });

  if (mode === 'submit') {
    if (!hasValue(normalized.legalName)) {
      addError(['legalName'], t('{name} is required', { name: t('Legal name') }));
    }
    if (!hasRequiredValue(payload.ein)) {
      addError(['ein'], t('{name} is required', { name: t('EIN') }));
    }
    if (!hasValue(normalized.legalEntityType)) {
      addError(['legalEntityType'], t('{name} is required', { name: t('Legal Entity Type') }));
    }

    requirement?.businessDocuments.forEach((obligation) => {
      if (obligation.minCount < 1) return;
      const fieldIndex = payload.businessInfoFiles?.findIndex((field) => field.obligationKey === obligation.key) ?? -1;
      const field: BusinessInfoFileFormValue | undefined = fieldIndex >= 0 ? payload.businessInfoFiles?.[fieldIndex] : undefined;
      if (visibleFileCount(field?.files) < obligation.minCount) {
        addError(['businessInfoFiles', Math.max(fieldIndex, 0), 'files'], t('{name} is required', { name: obligation.label }));
      }
    });

    normalized.principals?.forEach((principal, principalIndex) => {
      if (!hasValue(principal.firstName))
        addError(['principals', principalIndex, 'firstName'], t('{name} is required', { name: t('First name') }));
      if (!hasValue(principal.lastName))
        addError(['principals', principalIndex, 'lastName'], t('{name} is required', { name: t('Last name') }));
      const rawPrincipal = payload.principals?.[principalIndex];
      if (!hasRequiredValue(rawPrincipal?.email))
        addError(['principals', principalIndex, 'email'], t('{name} is required', { name: t('Email') }));
      if (!hasRequiredValue(rawPrincipal?.phone))
        addError(['principals', principalIndex, 'phone'], t('{name} is required', { name: t('Phone') }));
      if (!hasRequiredValue(rawPrincipal?.ssn))
        addError(['principals', principalIndex, 'ssn'], t('{name} is required', { name: t('SSN') }));
      if (!hasValue(principal.type)) addError(['principals', principalIndex, 'type'], t('{name} is required', { name: t('Type') }));

      requirement?.principalDocuments.forEach((obligation) => {
        if (obligation.minCount < 1 || !principalRequiresObligation(principal, obligation.principalScope)) return;
        const fieldIndex = principal.principalFiles?.findIndex((field) => field.obligationKey === obligation.key) ?? -1;
        const field: PrincipalFileFormValue | undefined = fieldIndex >= 0 ? principal.principalFiles?.[fieldIndex] : undefined;
        if (visibleFileCount(field?.files) < obligation.minCount) {
          addError(
            ['principals', principalIndex, 'principalFiles', Math.max(fieldIndex, 0), 'files'],
            t('{name} is required', { name: obligation.label })
          );
        }
      });
    });
  }

  return {
    valid: Object.keys(fieldErrors).length === 0,
    firstErrorPath,
    fieldErrors,
  };
};

export const applyCompanyBusinessInfoValidationErrors = (
  form: CompanyBusinessInfoErrorForm,
  result: CompanyBusinessInfoValidationResult
) => {
  form.clearValidate();
  Object.entries(result.fieldErrors).forEach(([key, message]) => {
    form.setFormError(keyToPath(key), message);
  });

  if (result.firstErrorPath && typeof document !== 'undefined') {
    requestAnimationFrame(() => {
      document.querySelector('.text-red-500')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
};
